import { useChat } from "@ai-sdk/react";
import { DefaultChatTransport } from "ai";
import {
  type FC,
  type FormEvent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import type { RepositoryChatConversation } from "../../dashboard-client";

import { useDashboardRealtime } from "../../hooks/useDashboardRealtime";
import { useProviderProgress } from "../../hooks/useProviderProgress";
import {
  type RepositoryChatRetrievalMode,
  getLatestTurnStatusSnapshot,
  isChatMessageData,
  mergeRetrievalStatuses,
  type RetrievalStatus,
  type RepositoryChatUiMessage,
  getConversationTitle,
  isRetrievalStatus,
  toRetrievalStatusMessage,
  toRepositoryChatUiMessages,
} from "./repositoryChatModels";
import {
  RepositoryChatComposer,
  RepositoryChatHeader,
  RepositoryChatTranscript,
} from "./RepositoryChatSections";
import { RepositoryChatTaskStatus } from "./RepositoryChatTaskStatus";

interface RepositoryChatProps {
  conversation: RepositoryChatConversation;
  onConversationChanged: () => Promise<void>;
  retrievalMode: RepositoryChatRetrievalMode;
}

export const RepositoryChat: FC<RepositoryChatProps> = ({
  conversation,
  onConversationChanged,
  retrievalMode,
}) => {
  const initialMessages = useMemo(
    () => toRepositoryChatUiMessages(conversation.messages),
    [conversation.messages],
  );
  const latestTurnSnapshot = useMemo(
    () => getLatestTurnStatusSnapshot(conversation),
    [conversation],
  );
  const [composerValue, setComposerValue] = useState("");
  const [retrievalStatus, setRetrievalStatus] =
    useState<RetrievalStatus | null>(null);
  const [isSendingTurn, setIsSendingTurn] = useState(false);
  const previousConversationIdRef = useRef(conversation.conversationId);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { subscribe } = useDashboardRealtime();
  const { getEventsForTask, getHydrationStatus, hydrateTask } =
    useProviderProgress();

  const transport = useMemo(
    () =>
      new DefaultChatTransport<RepositoryChatUiMessage>({
        api: `/user/source-control/chat/conversations/${conversation.conversationId}/messages/stream`,
        body: { baseRepoScope: conversation.repositoryScope, retrievalMode },
      }),
    [conversation.conversationId, conversation.repositoryScope, retrievalMode],
  );

  const [chatPaused, setChatPaused] = useState(false);

  const { messages, sendMessage, setMessages, status } =
    useChat<RepositoryChatUiMessage>({
      id: conversation.conversationId,
      messages: initialMessages,
      onData: (part) => {
        if (
          part.type === "data-retrieval-status" &&
          isRetrievalStatus(part.data)
        ) {
          setRetrievalStatus(part.data);
        }
        if (part.type === "data-chat:message" && isChatMessageData(part.data)) {
          if (part.data.final && part.data.paused) {
            setChatPaused(true);
          }
        }
      },
      transport,
    });

  const activeRetrievalStatus = mergeRetrievalStatuses({
    liveStatus: retrievalStatus,
    snapshot: latestTurnSnapshot,
  });
  const activeTaskId = activeRetrievalStatus?.taskId ?? null;
  const providerEvents = activeTaskId ? getEventsForTask(activeTaskId) : [];
  const providerHydrationStatus = activeTaskId
    ? getHydrationStatus(activeTaskId)
    : "idle";
  const isBusy =
    !chatPaused &&
    (isSendingTurn || status === "streaming" || status === "submitted");

  useEffect(() => {
    const conversationChanged =
      previousConversationIdRef.current !== conversation.conversationId;
    previousConversationIdRef.current = conversation.conversationId;

    if (conversationChanged) {
      setMessages(initialMessages);
      return;
    }

    if (status === "streaming" || status === "submitted") {
      return;
    }

    if (!shouldAdoptPersistedMessages(messages, initialMessages)) {
      return;
    }

    setMessages(initialMessages);
  }, [
    conversation.conversationId,
    initialMessages,
    messages,
    setMessages,
    status,
  ]);

  useEffect(() => {
    setRetrievalStatus(null);
    setChatPaused(false);
  }, [conversation.conversationId]);

  useEffect(() => {
    const unsubscribe = subscribe(
      (message) => {
        if (message.type !== "repository_chat_snapshot") {
          return;
        }

        const conversationId = message.payload.conversation_id;
        if (conversationId !== conversation.conversationId) {
          return;
        }

        if (message.payload.reason === "assistant_message_persisted") {
          void onConversationChanged();
          return;
        }

        if (
          message.payload.reason !== "turn_updated" ||
          typeof message.payload.turn_id !== "string" ||
          !isRepositoryChatTurnStatus(message.payload.status)
        ) {
          return;
        }

        setRetrievalStatus({
          message: toRetrievalStatusMessage(message.payload.status),
          status: message.payload.status,
          taskId:
            typeof message.payload.task_id === "string"
              ? message.payload.task_id
              : null,
          turnId: message.payload.turn_id,
        });
      },
      { topic: "internalEventTopic" },
    );

    return () => unsubscribe();
  }, [conversation.conversationId, onConversationChanged, subscribe]);

  useEffect(() => {
    if (!activeTaskId) {
      return;
    }

    void hydrateTask(activeTaskId);
  }, [activeTaskId, hydrateTask]);

  useEffect(() => {
    scrollRef.current?.scrollTo({
      behavior: "smooth",
      top: scrollRef.current.scrollHeight,
    });
  }, [messages, activeRetrievalStatus]);

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const nextMessage = composerValue.trim();
    if (!nextMessage) {
      return;
    }

    setComposerValue("");
    setRetrievalStatus(null);
    setChatPaused(false);
    setIsSendingTurn(true);

    try {
      await sendMessage({ text: nextMessage });
      await onConversationChanged();
    } finally {
      setIsSendingTurn(false);
    }
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden bg-white dark:bg-arti-panel">
      <RepositoryChatHeader
        repositoryCount={conversation.repositoryScope.length}
        retrievalMode={retrievalMode}
        title={getConversationTitle(conversation)}
      />
      <RepositoryChatTranscript
        isBusy={isBusy}
        messages={messages}
        scrollRef={scrollRef}
        taskStatusSlot={
          <RepositoryChatTaskStatus
            events={providerEvents}
            hydrationStatus={providerHydrationStatus}
            isBusy={isBusy}
            repositoryCount={conversation.repositoryScope.length}
            retrievalStatus={activeRetrievalStatus}
            turnSnapshot={latestTurnSnapshot}
          />
        }
      />
      <RepositoryChatComposer
        composerValue={composerValue}
        isBusy={isBusy}
        onComposerValueChange={setComposerValue}
        onSubmit={handleSubmit}
      />
    </div>
  );
};

function isRepositoryChatTurnStatus(
  value: unknown,
): value is RetrievalStatus["status"] {
  return (
    value === "pending" ||
    value === "queued" ||
    value === "running" ||
    value === "succeeded" ||
    value === "failed" ||
    value === "timed_out"
  );
}

function shouldAdoptPersistedMessages(
  currentMessages: RepositoryChatUiMessage[],
  persistedMessages: RepositoryChatUiMessage[],
): boolean {
  if (persistedMessages.length === 0) {
    return false;
  }

  if (currentMessages.length === 0) {
    return true;
  }

  if (persistedMessages.length > currentMessages.length) {
    return true;
  }

  const latestPersistedMessageId = persistedMessages.at(-1)?.id;
  if (!latestPersistedMessageId) {
    return false;
  }

  return !currentMessages.some(
    (message) => message.id === latestPersistedMessageId,
  );
}
